import { formatRate, IconStar, Rating } from '@leland-dev/leland-ui-library';
import Link from 'next/link';
import { type FC, useMemo } from 'react';

import { type GoalName } from '../../../__generated-gql-types__/globalTypes';
import { getReviewsAverage } from '../../../utils/reviews';
import { getReviewsPageUrl } from '../../../utils/routing';

export interface ReviewsBreakdownProps {
  reviewsByStar: number[];
  totalReviews: number;
  goalSearch: Possible<GoalName>;
  ratingSearch: Possible<number>;
}
export const ReviewsBreakdown: FC<ReviewsBreakdownProps> = ({
  reviewsByStar,
  totalReviews,
  goalSearch,
  ratingSearch,
}) => {
  const rating = useMemo(
    () => getReviewsAverage(totalReviews, reviewsByStar),
    [reviewsByStar, totalReviews],
  );

  return (
    <div className="min-w-80">
      <div className="flex flex-col border-b border-leland-gray-stroke py-4">
        <span className="text-left text-2xl font-medium">Overall rating</span>
        <div className="flex items-center gap-2">
          <span className="text-4xl font-medium">{formatRate(rating)}</span>
          <Rating rate={rating} />
        </div>
      </div>
      <div className="py-2">
        {reviewsByStar
          .slice()
          .reverse()
          .map((count, index) => (
            <RatingSlider
              rating={5 - index}
              count={count}
              total={totalReviews}
              key={index}
              active={ratingSearch === 5 - index}
              href={getReviewsPageUrl(
                goalSearch,
                ratingSearch === 5 - index ? null : 5 - index,
              )}
            />
          ))}
      </div>
    </div>
  );
};

interface RatingSliderProps {
  rating: number;
  count: number;
  total: number;
  active?: boolean;
  href: string;
}
const RatingSlider: FC<RatingSliderProps> = ({
  rating,
  count,
  total,
  active,
  href,
}) => {
  const width = `${(count / total) * 100}%`;
  return (
    <Link
      className={`grid w-full grid-cols-15 items-center gap-2 rounded-md p-2 hover:cursor-pointer ${active ? 'bg-leland-primary-extra-light' : 'hover:bg-leland-gray-hover'}`}
      href={href}
      id="reviews_breakdown--view_rating"
    >
      <div className="col-span-2 flex items-center gap-1 p-0">
        <IconStar className="size-4 shrink-0" />
        <span>{rating}</span>
      </div>
      <div
        className={`relative col-span-12 h-1.5 w-full rounded-full ${active ? 'bg-leland-primary-light' : 'bg-leland-gray-stroke'}`}
      >
        <div
          className="absolute inset-0 rounded-full bg-leland-black"
          style={{ width }}
        />
      </div>
      <span className="col-span-1 text-left font-medium">
        {count >= 1000 ? `${Math.floor(count / 1000)}k` : count}
      </span>
    </Link>
  );
};
